import ButtonBase from "@mui/material/ButtonBase";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { TachographSummaryFilter } from "../models/Tachograph";

export type TachographSummary = {
  total: number;
  downloadsDue: number;
  inspectionsDue: number;
  connectorsIssue: number;
};

type Props = {
  summary: TachographSummary;
  activeFilter: TachographSummaryFilter;
  onSelect: (filter: TachographSummaryFilter) => void;
};

type CardProps = {
  label: string;
  value: number;
  helper: string;
  isActive: boolean;
  onClick: () => void;
};

function SummaryCard({ label, value, helper, isActive, onClick }: CardProps) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={isActive}
      sx={{ flex: 1, minWidth: 220, borderRadius: 2, textAlign: "left" }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          height: "100%",
          p: 2,
          borderRadius: 2,
          borderColor: isActive ? "primary.main" : undefined,
          borderWidth: isActive ? 2 : 1,
          bgcolor: isActive ? "action.selected" : undefined,
        }}
      >
        <Stack spacing={0.75}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        </Stack>
      </Paper>
    </ButtonBase>
  );
}

/** The four counters above the table. Each one narrows the list down to what it counts. */
export function TachographSummaryCards({ summary, activeFilter, onSelect }: Props) {
  const { t } = useTranslation();

  const cards: Array<{ filter: TachographSummaryFilter; value: number; name: string }> = [
    { filter: "all", value: summary.total, name: "totalVehicles" },
    { filter: "downloads", value: summary.downloadsDue, name: "downloadsDue" },
    { filter: "inspections", value: summary.inspectionsDue, name: "inspectionsDue" },
    { filter: "connectors", value: summary.connectorsIssue, name: "connectorsIssue" },
  ];

  return (
    <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5}>
      {cards.map(card => (
        <SummaryCard
          key={card.filter}
          label={t(`tachograph.summary.${card.name}`)}
          value={card.value}
          helper={t(`tachograph.summary.${card.name}Hint`)}
          isActive={activeFilter === card.filter}
          onClick={() => onSelect(card.filter)}
        />
      ))}
    </Stack>
  );
}
